import {
  Card,
  CardActionArea,
  CardContent,
  Stack,
  Typography,
} from "@mui/material";
import type { ReactNode } from "react";

import { ChatBubbleOutlineOutlined } from "@mui/icons-material";

// Define the NewConversationRequest type locally
interface NewConversationRequest {
  id: number;
  name?: string | null;
  create_date: string;
  /** Parts AI dialogue kind ("run" | "comparison" | "search"); absent for other products. */
  kind?: string | null;
}
import { timeAgo } from "src/reusable/Functions";
import { ResourceStatusIndicator } from "src/features/notifications";

type RecentChatCardProps = {
  dialogue: NewConversationRequest;
  hideIcon?: boolean;
  type?: string;
  onClick: (dialogue: NewConversationRequest) => void;
  /**
   * Optional per-row badge, e.g. a chat-kind chip. This component is shared
   * across products, so product-specific UI (like Parts AI's kind chip) is
   * injected by the caller instead of imported here.
   */
  renderBadge?: (dialogue: NewConversationRequest) => ReactNode;
};

const RecentChatCard = ({
  dialogue,
  hideIcon = false,
  type = "none",
  onClick,
  renderBadge,
}: RecentChatCardProps) => {
  const lastMessage = timeAgo(String(dialogue.create_date));

  return (
    <Card
      variant="outlined"
      onClick={() => onClick(dialogue)}
      data-testid={`aa-history-item-${dialogue.id}`}
      data-attr={`assistant:card:open-recent-chat-${dialogue.id}`}
      sx={{
        height: "100%",
        cursor: "pointer",
      }}
    >
      <CardActionArea
        sx={{
          height: "100%",
        }}
        data-testid={`aa-history-item-action-${dialogue.id}`}
      >
        <CardContent
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            height: "100%",
          }}
        >
          <Stack
            direction={type === "all" ? "row" : "column"}
            gap={type === "all" ? 1.5 : 1}
            alignItems={type === "all" ? "center" : "flex-start"}
            justifyContent={type === "all" ? "flex-start" : "flex-start"}
            sx={{
              mb: 1,
            }}
          >
            {!hideIcon && (
              <ChatBubbleOutlineOutlined
                sx={{ color: "text.secondary" }}
                fontSize="small"
              />
            )}
            <Typography variant="body1">
              {dialogue.name ?? `Dialogue ${dialogue.id}`}
            </Typography>
            {renderBadge?.(dialogue)}
          </Stack>

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap={1}
          >
            <Typography variant="body2" color="text.secondary">
              {type === "all" ? `Last message ${lastMessage}` : lastMessage}
            </Typography>
            <ResourceStatusIndicator type="dialogue" id={dialogue.id} />
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export default RecentChatCard;
